import React, { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Box,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import { UsersContext } from '../context';
import { AppContext } from 'views/App/context';
import { translate as t } from 'hooks/useTranslations';
import useOrganization, { shouldLoadOrg } from 'views/App/Portal/LexID/Organizations/_data_sources/useOrganization';
import useOrganizationUser from 'views/App/Portal/LexID/Users/_data_sources/useOrganizationUser';
import { Form } from 'components/_forms';
import { User } from 'views/App/Portal/LexID/Users/_types/user';
import { type ServerErrorResponse } from 'data_sources/constants';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import errorHelper from 'helpers/errorHelper';
import datetimeHelper from 'helpers/datetimeHelper';

const EditUser: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { id } = useParams();
  const { updateUser, userSchema } = useContext(UsersContext);
  const { currentOrganizationId, hasPermission } = useContext(AppContext);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();
  const [isSaving, setIsSaving] = useState(false);
  const [ssoOrg, setSsoOrg] = useState<boolean>(false);
  const userQuery = useOrganizationUser(currentOrganizationId || '', id || '');
  const { isLoading: isUserLoading, isError: isUserError } = userQuery;
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
  } = useForm<User>({
    values: {
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      phone: '',
      alias: '',
      username: '',
      jobTitle: '',
      ssoLinkValue: '',
      organizationUserId: '',
      birthdate: '',
      active: true,
    },
    resolver: yupResolver(userSchema),
  });

  const handleSave: SubmitHandler<User> = (values: User) => {
    setIsSaving(true);
    const payload: User = {
      ...values,
      birthdate: values.birthdate?.trim() ? values.birthdate.trim() : '',
    };
    updateUser(
      id || '',
      payload,
      (data: User) => {
        navigate('..');
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setServerErrors(errors);
      }
    );
  };

  React.useEffect(() => {
    if (userQuery.data?.lastName) {
      reset({
        ...userQuery.data,
        birthdate: datetimeHelper.apiValueToBirthdateFormString(userQuery.data.birthdate) || '',
      });
    }
  }, [reset, userQuery.data]);

  const orgQuery = useOrganization(currentOrganizationId || '', {
    enabled: shouldLoadOrg(hasPermission, currentOrganizationId),
  });

  React.useEffect(() => {
    if (orgQuery.data && orgQuery.data?.cognitoPool?.authenticationType === 'SSO') {
      setSsoOrg(true);
    } else {
      setSsoOrg(false);
    }
  }, [orgQuery.data]);

  const nameParts = [userQuery.data?.firstName, userQuery.data?.middleName, userQuery.data?.lastName].filter(Boolean);
  const pageTitle =
    nameParts.length > 0 ? `${t('users.edit.title')}: ${nameParts.join(' ')}` : t('users.edit.title');

  return (
    <TakeoverLayout
      pageTitle={pageTitle}
      backUrl={`../${id}`}
    >
      <Card
        sx={{ maxWidth: 800, mx: 'auto', height: '100%' }}
        className='lex-form'
      >
        {isUserLoading && (
          <Stack
            direction='row'
            justifyContent='center'
            sx={{ py: 6 }}
          >
            <CircularProgress data-testid='edit-user-loading' />
          </Stack>
        )}
        {isUserError && (
          <CardContent>
            {errorHelper.renderErrors(userQuery)}
            <Button
              color='neutral'
              href={`../${id}`}
              sx={{ mt: 2 }}
            >
              {t('common.cancel')}
            </Button>
          </CardContent>
        )}
        {!isUserLoading && !isUserError && (
          <Form onSubmit={handleSubmit(handleSave)}>
            <CardContent>
              {errorHelper.renderAllErrors(serverErrors)}
              <Grid
                container
                columnSpacing={4}
                rowGap={6}
              >
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  id='firstName'
                  key='firstName'
                  label={t('users.properties.firstName')}
                  variant='outlined'
                  required
                  autoFocus
                  fullWidth
                  {...register('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName ? errors.firstName.message : undefined}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 2 }}>
                <TextField
                  id='middleName'
                  key='middleName'
                  label={t('users.properties.middleName')}
                  variant='outlined'
                  fullWidth
                  {...register('middleName')}
                  error={!!errors.middleName}
                  helperText={errors.middleName ? errors.middleName.message : undefined}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  id='lastName'
                  key='lastName'
                  label={t('users.properties.lastName')}
                  variant='outlined'
                  required
                  fullWidth
                  {...register('lastName')}
                  error={!!errors.lastName}
                  helperText={errors.lastName ? errors.lastName.message : undefined}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id='email'
                  key='email'
                  label={t('users.properties.email')}
                  variant='outlined'
                  fullWidth
                  required
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email ? errors.email.message : undefined}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name='birthdate'
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <DatePicker
                      label={t('users.properties.birthdate')}
                      format='MM-DD-YYYY'
                      value={datetimeHelper.birthdateToDayjs(value)}
                      onChange={(newValue) => {
                        onChange(datetimeHelper.dayjsToBirthdateFormString(newValue));
                      }}
                      maxDate={dayjs()}
                      slotProps={{
                        field: { clearable: true, onClear: () => onChange('') },
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          error: !!errors.birthdate,
                          helperText: errors.birthdate?.message,
                          placeholder: 'MM-DD-YYYY',
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              {ssoOrg && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    id='ssoLinkValue'
                    key='ssoLinkValue'
                    label={t('users.properties.ssoLinkValue')}
                    variant='outlined'
                    fullWidth
                    {...register('ssoLinkValue')}
                    error={!!errors.ssoLinkValue}
                    helperText={errors.ssoLinkValue ? errors.ssoLinkValue.message : undefined}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id='username'
                  key='username'
                  label={t('users.properties.username')}
                  variant='outlined'
                  fullWidth
                  {...register('username')}
                  error={!!errors.username}
                  helperText={errors.username ? errors.username.message : undefined}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id='organizationUserId'
                  key='organizationUserId'
                  label={t('users.properties.organizationUserId')}
                  variant='outlined'
                  fullWidth
                  {...register('organizationUserId')}
                  error={!!errors.organizationUserId}
                  helperText={
                    errors.organizationUserId
                      ? errors.organizationUserId.message
                      : t('users.properties.organizationUserIdHint')
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id='jobTitle'
                  key='jobTitle'
                  label={t('users.properties.jobTitle')}
                  variant='outlined'
                  fullWidth
                  {...register('jobTitle')}
                  error={!!errors.jobTitle}
                  helperText={errors.jobTitle ? errors.jobTitle.message : undefined}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id='alias'
                  key='alias'
                  label={t('users.properties.alias')}
                  variant='outlined'
                  fullWidth
                  {...register('alias')}
                  error={!!errors.alias}
                  helperText={errors.alias ? errors.alias.message : undefined}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id='phone'
                  key='phone'
                  label={t('users.properties.phone')}
                  variant='outlined'
                  fullWidth
                  {...register('phone')}
                  error={!!errors.phone}
                  helperText={errors.phone ? errors.phone.message : undefined}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 12 }}>
                <Box sx={{ pt: 1, px: 3 }}>
                  <Controller
                    name='active'
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            id='active'
                            checked={value}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                              if (!!checked) {
                                onChange(checked);
                              } else {
                                confirm({
                                  cancellationButtonProps: {
                                    variant: 'outlined',
                                    color: 'neutral',
                                  },
                                  confirmationButtonProps: {
                                    variant: 'contained',
                                    color: 'error',
                                  },
                                  confirmationText: t('users.edit.deactivateConfirmOk'),
                                  description: t('users.edit.deactivateConfirm'),
                                  title: t('users.edit.deactivateConfirmTitle'),
                                })
                                  .then(() => {
                                    onChange(checked);
                                  })
                                  .catch(() => {
                                    event.preventDefault();
                                    return false;
                                  });
                              }
                            }}
                          />
                        }
                        label={t('users.properties.active')}
                      />
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Box />
            <Stack
              direction='row'
              spacing={2}
            >
              <Button
                color='neutral'
                href='..'
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => {
                  trigger();
                }}
                disabled={isSaving}
                loading={isSaving}
                loadingPosition='end'
                component='button'
                type='submit'
                variant='contained'
              >
                {t('users.edit.save')}
              </Button>
            </Stack>
          </CardActions>
        </Form>
        )}
      </Card>
    </TakeoverLayout>
  );
};

export default EditUser;
